'use client';

import type { StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { createTrackedStore } from '@/lib/dev/zustand-registry';
import { logger } from '@/lib/telemetry/logger';

export const MAX_RECENT_ENTRIES: number = 6;
export const RECENT_SEARCHES_STORAGE_KEY: string = 'morfi-recent-searches';
export const RECENT_SEARCHES_VERSION: number = 1;

export interface RecentSearchEntry {
  query: string;
  ts: number;
}

interface RecentSearchesState {
  entries: RecentSearchEntry[];
  addRecent: (query: string) => void;
  clearRecent: () => void;
}

// Tolerant Storage wrapper: localStorage may throw on setItem in private mode
// or under quota pressure. FR-009 requires silent degradation — the in-memory
// state update must succeed even when persistence fails. We swallow the error
// here and emit a single observable warning via the logger.
function createSafeStorage(): Storage {
  return {
    get length(): number {
      try {
        return localStorage.length;
      } catch {
        // SILENT-CATCH: storage unavailable (private mode / disabled) — FR-009 mandates silent degradation; setItem-path already logs once.
        return 0;
      }
    },
    clear(): void {
      try {
        localStorage.clear();
      } catch {
        // SILENT-CATCH: same as length — silent under FR-009.
      }
    },
    getItem(key: string): string | null {
      try {
        return localStorage.getItem(key);
      } catch (error: unknown) {
        logger.warn('recent-searches.persist_unavailable', {
          op: 'getItem',
          message: error instanceof Error ? error.message.slice(0, 200) : 'unknown',
        });
        return null;
      }
    },
    key(index: number): string | null {
      try {
        return localStorage.key(index);
      } catch {
        // SILENT-CATCH: storage unavailable — silent under FR-009; setItem-path already logs once.
        return null;
      }
    },
    removeItem(key: string): void {
      try {
        localStorage.removeItem(key);
      } catch {
        // SILENT-CATCH: storage unavailable — silent under FR-009.
      }
    },
    setItem(key: string, value: string): void {
      try {
        localStorage.setItem(key, value);
      } catch (error: unknown) {
        logger.warn('recent-searches.persist_unavailable', {
          op: 'setItem',
          message: error instanceof Error ? error.message.slice(0, 200) : 'unknown',
        });
      }
    },
  };
}

export const useRecentSearchesStore: UseBoundStore<StoreApi<RecentSearchesState>> =
  createTrackedStore<RecentSearchesState>('useRecentSearchesStore')(
    persist(
      (set, get) => ({
        entries: [],

        addRecent: (query: string): void => {
          const trimmed = query.trim();
          if (trimmed.length === 0) return;

          const lower = trimmed.toLocaleLowerCase('en-US');
          const filtered = get().entries.filter(
            (entry) => entry.query.toLocaleLowerCase('en-US') !== lower,
          );
          const next: RecentSearchEntry[] = [{ query: trimmed, ts: Date.now() }, ...filtered].slice(
            0,
            MAX_RECENT_ENTRIES,
          );

          set({ entries: next });
        },

        clearRecent: (): void => {
          set({ entries: [] });
        },
      }),
      {
        name: RECENT_SEARCHES_STORAGE_KEY,
        version: RECENT_SEARCHES_VERSION,
        storage: createJSONStorage(() => createSafeStorage()),
        onRehydrateStorage:
          () =>
          // ALLOW-UNKNOWN: zustand persist's onRehydrateStorage callback signature uses `unknown` for error.
          (_state: RecentSearchesState | undefined, error: unknown): void => {
            if (error !== undefined && error !== null) {
              logger.warn('recent-searches.persist_unavailable', {
                op: 'rehydrate',
                message: error instanceof Error ? error.message.slice(0, 200) : 'unknown',
              });
            }
          },
      },
    ),
  );
